import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import {
  Search, Layers, LayoutDashboard, CheckCircle2, FileBarChart, MessageSquare,
  ChevronDown, ArrowUp, IndianRupee, MapPin, Zap,
} from 'lucide-react';

// Backend-resolved role is the source of truth.
// Login form NEVER asks for or sends role — it just authenticates.
const POST_LOGIN_ROUTE = {
  brand: '/brand/dashboard',
  creator: '/dashboard',
  account_executive: '/admin/incoming',
  admin: '/admin/incoming',
  campaign_manager: '/admin/incoming',
  talent_manager: '/talent/dashboard',
  manager: '/admin/incoming',
  agency: '/agency/dashboard',
};

// ============================================================================
// Feature-carousel data
// ============================================================================
const FEATURES = [
  {
    key: 'discovery',
    icon: Search,
    name: 'Creator Discovery',
    desc: 'Search 19,000+ vetted Indian creators by category, location, language and price.',
    mockup: 'discovery',
    color: '#FF451A',
  },
  {
    key: 'campaign',
    icon: Layers,
    name: 'Campaign Management',
    desc: 'Track every creator from pitched to live in real time.',
    mockup: 'pipeline',
    color: '#0055FF',
  },
  {
    key: 'dashboard',
    icon: LayoutDashboard,
    name: 'Brand Dashboard',
    desc: 'Brands see exactly where their campaign stands without a single WhatsApp message.',
    mockup: 'dashboard',
    color: '#00C972',
  },
  {
    key: 'approvals',
    icon: CheckCircle2,
    name: 'Deliverable Approvals',
    desc: 'Approve or revise content directly in platform. Full revision history kept.',
    mockup: 'approvals',
    color: '#FFB800',
  },
  {
    key: 'reports',
    icon: FileBarChart,
    name: 'Campaign Reports',
    desc: 'Auto-generated PDF and Excel reports with live links and performance metrics.',
    mockup: 'reports',
    color: '#9B6EFF',
  },
  {
    key: 'whatsapp',
    icon: MessageSquare,
    name: 'WhatsApp Integration',
    desc: 'Bulk message 200 creators in one click. Automated status notifications.',
    mockup: 'whatsapp',
    color: '#25D366',
  },
];

// Self-contained keyframes + smooth-scroll declaration
const INLINE_STYLES = `
html { scroll-behavior: smooth; }
@keyframes strawhat-caret { 0%, 50% { opacity: 1; } 50.01%, 100% { opacity: 0; } }
@keyframes strawhat-slide-in-up { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes strawhat-progress-fill { 0% { width: 8%; } 100% { width: 92%; } }
@keyframes strawhat-metric-count { 0% { opacity: 0.4; } 100% { opacity: 1; } }
@keyframes strawhat-pulse-dot {
  0%, 100% { transform: translateY(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(220px); opacity: 0; }
}
@keyframes strawhat-flip {
  0%, 45% { transform: rotateX(0deg); }
  50%, 100% { transform: rotateX(180deg); }
}
@keyframes strawhat-scroll-hint {
  0%, 100% { transform: translateY(0); opacity: .6; }
  50% { transform: translateY(4px); opacity: 1; }
}
.strawhat-caret { animation: strawhat-caret 1s steps(2, start) infinite; }
.strawhat-progress-fill { animation: strawhat-progress-fill 3s ease-in-out infinite alternate; }
.strawhat-scroll-hint { animation: strawhat-scroll-hint 1.8s ease-in-out infinite; }
`;

// ============================================================================
// Mockup panels — pure CSS/SVG per-feature visuals
// ============================================================================
const DiscoveryMockup = () => (
  <div className="w-full h-full flex flex-col gap-3 p-4">
    <div className="bg-black/[0.03] border border-black/[0.08] rounded-lg px-3 py-2 flex items-center gap-2">
      <Search size={12} className="text-[#6B6B6B]" />
      <span className="text-xs font-mono text-[#0F0F0F]">beauty · mumbai · hindi</span>
      <span className="strawhat-caret w-[1.5px] h-3 bg-[#FF451A] inline-block" />
    </div>
    <div className="space-y-1.5">
      {['Priya Sharma · 145K', 'Karan Bhatia · 68K', 'Tanya Kapoor · 850K', 'Vikram Singh · 360K'].map((n, i) => (
        <div
          key={n}
          className="bg-[#F7F7F8] border border-black/[0.06] rounded-md px-2 py-1.5 flex items-center gap-2"
          style={{ animation: `strawhat-slide-in-up 0.6s ease-out ${i * 0.15}s both` }}
        >
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF]" />
          <span className="text-[10px] font-mono text-[#0F0F0F]">{n}</span>
        </div>
      ))}
    </div>
  </div>
);

const PipelineMockup = () => {
  const stages = ['Invited', 'Interested', 'Confirmed', 'Live', 'Completed'];
  const colors = ['#0055FF', '#9B6EFF', '#00C972', '#FF451A', '#22C55E'];
  return (
    <div className="w-full h-full p-4 flex flex-col justify-center gap-2">
      {stages.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span className="w-24 text-[10px] font-mono uppercase tracking-wider text-[#6B6B6B] text-right">{s}</span>
          <div className="flex-1 h-2 bg-black/[0.03] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full strawhat-progress-fill"
              style={{ backgroundColor: colors[i], animationDelay: `${i * 0.2}s` }}
            />
          </div>
          <span className="w-6 text-[10px] font-mono text-[#6B6B6B]">{Math.max(1, 12 - i * 2)}</span>
        </div>
      ))}
    </div>
  );
};

const DashboardMockup = () => {
  const tiles = [
    { l: 'Views',      v: '182K', c: '#06B6D4' },
    { l: 'Reach',      v: '95.4K', c: '#FFB800' },
    { l: 'Engagement', v: '8.4%', c: '#FF451A' },
    { l: 'Creators',   v: '24/30', c: '#00C972' },
  ];
  return (
    <div className="w-full h-full grid grid-cols-2 gap-2 p-4">
      {tiles.map((t, i) => (
        <div
          key={t.l}
          className="bg-[#F7F7F8] border border-black/[0.06] rounded-lg p-3 flex flex-col justify-center"
          style={{ animation: `strawhat-metric-count 0.8s ease-out ${i * 0.15}s both` }}
        >
          <div className="text-[9px] font-mono uppercase tracking-wider text-[#6B6B6B] mb-0.5">{t.l}</div>
          <div className="text-xl font-bold font-mono" style={{ color: t.c }}>{t.v}</div>
        </div>
      ))}
    </div>
  );
};

const ApprovalsMockup = () => (
  <div className="w-full h-full p-4 flex flex-col justify-center items-center gap-3">
    <div
      className="w-56 h-32 bg-[#F7F7F8] border border-black/[0.08] rounded-xl relative"
      style={{ transformStyle: 'preserve-3d', animation: 'strawhat-flip 4s ease-in-out infinite' }}
    >
      <div className="absolute inset-0 p-3 backface-hidden">
        <div className="text-[9px] font-mono uppercase tracking-wider text-[#FFB800] mb-1">PENDING</div>
        <div className="text-xs text-[#0F0F0F] font-semibold mb-2">Reel_v1_final.mp4</div>
        <div className="h-1 bg-black/[0.03] rounded-full mb-1"><div className="h-full w-3/4 bg-[#FFB800] rounded-full" /></div>
        <div className="h-1 bg-black/[0.03] rounded-full mb-1"><div className="h-full w-1/2 bg-black/[0.10] rounded-full" /></div>
      </div>
      <div
        className="absolute inset-0 p-3"
        style={{ transform: 'rotateX(180deg)', backfaceVisibility: 'hidden' }}
      >
        <div className="text-[9px] font-mono uppercase tracking-wider text-[#00C972] mb-1 flex items-center gap-1">
          <CheckCircle2 size={9} /> APPROVED
        </div>
        <div className="text-xs text-[#0F0F0F] font-semibold mb-2">Reel_v2_final.mp4</div>
        <div className="h-1 bg-[#00C972]/30 rounded-full mb-1"><div className="h-full w-full bg-[#00C972] rounded-full" /></div>
        <div className="text-[9px] text-[#6B6B6B] font-mono">Rev history · 2 rounds</div>
      </div>
    </div>
  </div>
);

const ReportsMockup = () => (
  <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-4">
    {[
      { label: 'PDF', color: '#FF3B30', y: 0 },
      { label: 'XLSX', color: '#00C972', y: 1 },
    ].map((f, i) => (
      <div
        key={f.label}
        className="flex items-center gap-3 bg-[#F7F7F8] border border-black/[0.08] rounded-lg px-4 py-2.5 w-56"
        style={{ animation: `strawhat-slide-in-up 0.8s ease-out ${i * 0.3}s both` }}
      >
        <div className="w-8 h-10 rounded" style={{ backgroundColor: `${f.color}20`, border: `1px solid ${f.color}60`, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FileBarChart size={14} />
        </div>
        <div className="flex-1">
          <div className="text-xs text-[#0F0F0F] font-semibold">{f.label} Report</div>
          <div className="text-[9px] font-mono text-[#6B6B6B]">6 creators · views · reach</div>
        </div>
        <div className="text-[10px] font-mono" style={{ color: f.color }}>↓</div>
      </div>
    ))}
  </div>
);

const WhatsAppMockup = () => (
  <div className="w-full h-full flex items-center justify-center p-4 relative">
    <div className="relative w-24 h-56">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="absolute left-0 flex items-center gap-2"
          style={{ top: `${i * 28}px` }}
        >
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF]" />
          <div className="w-12 h-1 bg-black/[0.06] rounded-full" />
        </div>
      ))}
      <div
        className="absolute left-2 w-3 h-3 rounded-full bg-[#25D366] shadow-[0_0_12px_rgba(37,211,102,0.8)]"
        style={{ animation: 'strawhat-pulse-dot 2.2s ease-in-out infinite' }}
      />
    </div>
  </div>
);

const MOCKUPS = {
  discovery: DiscoveryMockup,
  pipeline: PipelineMockup,
  dashboard: DashboardMockup,
  approvals: ApprovalsMockup,
  reports: ReportsMockup,
  whatsapp: WhatsAppMockup,
};

// ============================================================================
// Main component
// ============================================================================
export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('influencer'); // UI-only: influencer | agency | brand
  const { login } = useAuth();
  const navigate = useNavigate();

  // Carousel state
  const [idx, setIdx] = useState(0);
  const [hovering, setHovering] = useState(false);
  const pausedUntil = useRef(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      const role = result.user?.role;
      navigate(POST_LOGIN_ROUTE[role] || '/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // Auto-rotate carousel — pauses on hover; also honours a temporary
  // grace window after a manual dot click.
  useEffect(() => {
    const t = setInterval(() => {
      if (hovering) return;
      if (Date.now() < pausedUntil.current) return;
      setIdx((i) => (i + 1) % FEATURES.length);
    }, 4000);
    return () => clearInterval(t);
  }, [hovering]);

  const goToSlide = (i) => {
    setIdx(i);
    pausedUntil.current = Date.now() + 8000; // 8s grace period
  };

  const scrollToLogin = () => {
    document.getElementById('login-hero')?.scrollIntoView({ behavior: 'smooth' });
  };

  const active = FEATURES[idx];
  const Mockup = MOCKUPS[active.mockup];

  return (
    <div className="min-h-screen bg-white text-[#0F0F0F] overflow-x-hidden">
      {/* Global keyframes + smooth-scroll — self-contained */}
      <style dangerouslySetInnerHTML={{ __html: INLINE_STYLES }} />

      {/* =================================================================== */}
      {/* Section 0 — Existing login form (UNCHANGED)                          */}
      {/* =================================================================== */}
      <section id="login-hero" className="min-h-screen flex bg-white text-[#0F0F0F] relative">
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-8">
                <span className="font-display text-2xl tracking-tight">
                  <span className="text-[#0F0F0F]">Vy</span><span className="text-[#FF451A] italic">vre</span>
                </span>
              </div>
              <h1 className="font-display text-5xl tracking-tight text-[#0F0F0F] mb-3 leading-tight">Welcome back.</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]">Sign in to your control room</p>
            </div>

            {/* Role selector — UI only. Backend auth ignores this and returns the
                real role from the DB. Used only to swap the sign-up footer copy. */}
            <div
              className="flex items-center gap-1.5 p-1 mb-6 bg-[#F0F0F0] rounded-full w-full"
              role="tablist"
              aria-label="Login role selector"
              data-testid="login-role-selector"
            >
              {[
                { key: 'influencer', label: 'Influencer' },
                { key: 'agency',     label: 'Agency' },
                { key: 'brand',      label: 'Brand' },
              ].map((r) => {
                const active = role === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setRole(r.key)}
                    className={`flex-1 px-3 py-2 rounded-full text-xs font-semibold uppercase tracking-[0.08em] transition-all ${
                      active
                        ? 'bg-[#FF451A] text-white shadow-[0_4px_14px_rgba(255,69,26,0.28)]'
                        : 'bg-transparent text-[#6B6B6B] hover:text-[#0F0F0F]'
                    }`}
                    data-testid={`role-pill-${r.key}`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              {error && (
                <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 rounded-xl text-sm" data-testid="login-error-message">
                  {error}
                </div>
              )}

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] block mb-2">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-[#F0F0F0] border border-black/[0.08] rounded-xl px-4 py-3 text-[#0F0F0F] placeholder:text-[#A1A1AA] focus:bg-white focus:border-[#FF451A]/50 focus:ring-2 focus:ring-[#FF451A]/20 transition-all"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] block mb-2">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-[#F0F0F0] border border-black/[0.08] rounded-xl px-4 py-3 text-[#0F0F0F] placeholder:text-[#A1A1AA] focus:bg-white focus:border-[#FF451A]/50 focus:ring-2 focus:ring-[#FF451A]/20 transition-all"
                  data-testid="login-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-tactile btn-tactile-primary w-full px-6 py-3.5 rounded-md disabled:opacity-50"
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-[#A1A1AA]" data-testid="login-internal-staff-note">
              Internal staff? Contact your administrator for access.
            </p>

            {role === 'influencer' ? (
              <p className="mt-4 text-center text-sm text-[#6B6B6B]">
                Don&apos;t have an account?{' '}
                <Link to="/register" className="text-[#FF451A] font-medium hover:text-[#E03D17] transition-colors" data-testid="register-link">
                  Sign up
                </Link>
              </p>
            ) : (
              <p
                className="mt-4 text-center text-xs text-[#6B6B6B] leading-relaxed max-w-xs mx-auto"
                data-testid={`admin-only-note-${role}`}
              >
                {role === 'agency' ? 'Agency' : 'Brand'} accounts are created by your StrawHat administrator.{' '}
                <Link to="/demo" className="text-[#FF451A] font-medium hover:text-[#E03D17] transition-colors">
                  Contact us to get access
                </Link>.
              </p>
            )}
          </div>
        </div>

        <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden bg-[#F7F7F8]" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(255,69,26,0.18) 0%, rgba(255,69,26,0.06) 30%, #F7F7F8 70%)' }}>
          <div className="absolute inset-0 bg-[url('https://static.prod-images.emergentagent.com/jobs/9de54d98-d977-4d52-98c6-87def6f7dbb6/images/270719a556826637cdb21fff7e6a54d54775e71246a523c63143781c80b4f5f3.png')] bg-cover bg-center opacity-[0.06] mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-[#FF451A]/10"></div>
          <div className="text-center text-[#0F0F0F] relative z-10 px-12">
            <h2 className="font-display text-7xl tracking-tight leading-[0.95] mb-4">
              Vy<span className="italic text-[#FF451A]">vre</span>
              <br /><span className="font-display text-3xl text-[#6B6B6B] italic">Creator Intelligence.</span>
            </h2>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#6B6B6B] mb-8">The premium talent operating system</p>
            <div className="space-y-3 text-left max-w-sm mx-auto font-body text-[#0F0F0F]">
              <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />Brand Discovery Engine</div>
              <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />AI Creator Matching</div>
              <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />Multi-tenant Workflows</div>
              <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />Invoicing in ₹ INR</div>
            </div>
          </div>
        </div>

        {/* Scroll-to-explore hint */}
        <a
          href="#hero-statement"
          className="hidden sm:flex absolute bottom-6 left-1/2 -translate-x-1/2 items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#6B6B6B] hover:text-[#0F0F0F] transition-colors group"
          data-testid="scroll-to-explore"
        >
          <span>Scroll to explore</span>
          <ChevronDown size={14} className="text-[#FF451A] strawhat-scroll-hint group-hover:text-[#0F0F0F]" />
        </a>
      </section>

      {/* =================================================================== */}
      {/* Section 1 — Hero statement                                            */}
      {/* =================================================================== */}
      <section
        id="hero-statement"
        className="relative bg-[#F7F7F8] py-24 md:py-36 px-6 md:px-12 lg:px-24 overflow-hidden"
        data-testid="hero-statement-section"
      >
        {/* Subtle grain / vignette */}
        <div
          className="absolute inset-0 pointer-events-none opacity-70"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,69,26,0.06) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(0,85,255,0.06) 0%, transparent 45%)',
          }}
        />
        <div className="relative max-w-5xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-6">
            Chapter I · The Platform
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#0F0F0F] mb-6">
            The Operating System for
            <br />
            <span className="italic text-[#FF451A]">Influencer Marketing.</span>
          </h2>
          <p className="text-lg md:text-xl text-[#6B6B6B] max-w-2xl leading-relaxed">
            From creator discovery to campaign completion — everything your team needs in one place.
          </p>
        </div>
      </section>

      {/* =================================================================== */}
      {/* Section 2 — Feature carousel                                          */}
      {/* =================================================================== */}
      <section
        className="bg-white py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-black/[0.05]"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        data-testid="features-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-3">Chapter II · Features</div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#0F0F0F] mb-12">
            Six pillars. One platform.
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center min-h-[320px]">
            {/* Left — text */}
            <div key={`text-${active.key}`} style={{ animation: 'strawhat-slide-in-up 0.5s ease-out both' }}>
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                style={{ backgroundColor: `${active.color}15`, border: `1px solid ${active.color}40` }}
              >
                <active.icon size={26} style={{ color: active.color }} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2">
                {String(idx + 1).padStart(2, '0')} / {String(FEATURES.length).padStart(2, '0')}
              </div>
              <h4 className="font-display text-3xl md:text-4xl tracking-tight text-[#0F0F0F] mb-3">
                {active.name}
              </h4>
              <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed max-w-md">
                {active.desc}
              </p>
            </div>

            {/* Right — animated mockup */}
            <div
              key={`mockup-${active.key}`}
              className="bg-[#F7F7F8] border border-black/[0.06] rounded-2xl h-72 md:h-80 relative overflow-hidden"
              style={{ animation: 'strawhat-slide-in-up 0.6s ease-out both' }}
            >
              <Mockup />
              <div
                className="absolute inset-x-0 bottom-0 h-1"
                style={{
                  background: `linear-gradient(90deg, transparent, ${active.color}, transparent)`,
                  opacity: 0.6,
                }}
              />
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-12" role="tablist" aria-label="Feature carousel">
            {FEATURES.map((f, i) => (
              <button
                key={f.key}
                role="tab"
                aria-selected={i === idx}
                aria-label={f.name}
                onClick={() => goToSlide(i)}
                className="group relative"
                data-testid={`feature-dot-${i}`}
              >
                <span
                  className="block h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: i === idx ? '32px' : '8px',
                    backgroundColor: i === idx ? active.color : '#D4D4D8',
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================================== */}
      {/* Section 3 — Stat row                                                  */}
      {/* =================================================================== */}
      <section
        className="bg-[#F7F7F8] py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-black/[0.05]"
        data-testid="stats-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-3">Chapter III · Scale</div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#0F0F0F] mb-12">
            Built for the Indian market.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { headline: '19,000+', label: 'Vetted Indian Creators',       icon: Zap },
              { headline: 'Pan India', label: 'North · South · East · West · Central', icon: MapPin },
              { headline: 'End to End', label: 'Brief to Invoice in One Platform',     icon: IndianRupee },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-white border border-black/[0.06] rounded-2xl p-8 hover:-translate-y-[1px] hover:border-[#FF451A]/20 transition-all"
                data-testid={`stat-${s.headline.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <s.icon size={18} className="text-[#FF451A] mb-4" />
                <div className="font-display text-5xl md:text-6xl text-[#FF451A] leading-none tracking-tight mb-3">
                  {s.headline}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B6B6B]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================================== */}
      {/* Section 4 — Closing CTA                                               */}
      {/* =================================================================== */}
      <section
        className="bg-white py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-black/[0.05]"
        data-testid="cta-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-6">Chapter IV · Begin</div>
          <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#0F0F0F] leading-[1.05] mb-10">
            Ready to run your
            <br />
            <span className="italic text-[#FF451A]">first campaign?</span>
          </h3>
          <button
            onClick={scrollToLogin}
            className="btn-tactile btn-tactile-primary px-8 py-3.5 rounded-md inline-flex items-center gap-2"
            data-testid="cta-sign-in-btn"
          >
            <ArrowUp size={14} /> Sign in
          </button>
          <p className="mt-16 text-[10px] font-mono uppercase tracking-[0.3em] text-[#A1A1AA]">
            © 2026 Vyvre · Made in Mumbai
          </p>
        </div>
      </section>
    </div>
  );
};
